import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useMessages } from '../hooks/useMessages';
import { useScreenSize } from '../hooks/useScreenSize';
import { User } from '../types';
import { isGifUrl } from '../utils/gifUtils';
import GifPicker from '../components/UI/GifPicker';

export const Messages: React.FC = () => {
  const { user } = useAuth();
  const { isMobile } = useScreenSize();
  
  // UI state for menu actions and attachment
  const [showMenu, setShowMenu] = useState(false);
  const [showAttachment, setShowAttachment] = useState(false);
  const [attachmentPreview, setAttachmentPreview] = useState<string|null>(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [hasUserClosedDrawer, setHasUserClosedDrawer] = useState(false);
  
  // Debug pour tracker les changements de showDrawer
  useEffect(() => {
    console.log('🔧 DEBUG: showDrawer state changé:', showDrawer);
  }, [showDrawer]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  // Notification (toast)
  const [toast, setToast] = useState<string|null>(null);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  
  // Mobile: state pour afficher les conversations ou le chat
  const [showConversationList, setShowConversationList] = useState(true);

  // Show toast on send
  const handleSendMessageWithToast = async () => {
    await handleSendMessage();
    setToast('Message envoyé !');
    setTimeout(() => setToast(null), 2000);
  };

  // Handle attachment
  const handleAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setAttachmentPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleSendAttachment = async () => {
    // TODO: send image to backend
    setToast('Image envoyée (mock) !');
    setAttachmentPreview(null);
    setTimeout(() => setToast(null), 2000);
  };
  
  const {
    conversations,
    selectedConversation,
    messages,
    loading,
    error,
    searchResults,
    unreadCount,
    onlineUsers,
    sendMessage,
    selectConversation,
    markAsRead,
    searchUsers,
    startNewConversation
  } = useMessages();
  
  const [newMessage, setNewMessage] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [conversationSearchTerm, setConversationSearchTerm] = useState<string>('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [selectedGif, setSelectedGif] = useState<string>('');
  const [showNewConversation, setShowNewConversation] = useState(false);
  // Ajout pour le modal nouvelle conversation
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const handleSendMessage = async () => {
    const messageContent = newMessage || selectedGif;
    if (!messageContent.trim() || !selectedConversation) return;

    // Trouver l'autre participant
    const otherParticipant = selectedConversation.participants.find((p: string) => p !== user?.id);
    if (!otherParticipant) return;

    try {
      // Détecter le type de message
      let messageType: 'text' | 'image' | 'gif' = 'text';
      let content = messageContent;
      
      if (selectedGif) {
        messageType = 'gif';
        content = selectedGif;
      } else if (isGifUrl(messageContent)) {
        messageType = 'gif';
      }
      
      await sendMessage(otherParticipant, content, messageType);
      setNewMessage('');
      setSelectedGif('');
      setShowGifPicker(false);
    } catch (error) {
      console.error('Erreur envoi message:', error);
    }
  };

  const handleGifSelect = (gifUrl: string) => {
    setSelectedGif(gifUrl);
    setShowGifPicker(false);
    setNewMessage(''); // Vider le texte si un GIF est sélectionné
  };

  const removeSelectedGif = () => {
    setSelectedGif('');
  };

  const handleSearchUsers = async (term: string) => {
    const safeTerm = term || '';
    setSearchTerm(safeTerm);
    if (safeTerm.trim()) {
      await searchUsers(safeTerm);
    }
  };

  const formatTime = (timestamp: any) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'maintenant';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    return `${diffDays}j`;
  };

  const getParticipantInfo = (conversation: any) => {
    const otherParticipantId = conversation.participants.find((p: string) => p !== user?.id);
    // En attendant d'avoir les données utilisateur, on utilise l'ID
    return {
      id: otherParticipantId,
      name: `Utilisateur ${otherParticipantId?.slice(-4)}`,
      avatar: '🐱‍🚀',
      isOnline: onlineUsers[otherParticipantId || ''] || false
    };
  };

  const cosmicEmojis = ['🚀', '🌟', '⭐', '💫', '🌌', '🐱‍🚀', '🛸', '💰', '🎯', '✨'];

  // Ouvrir automatiquement le drawer sur mobile s'il y a des conversations
  useEffect(() => {
    console.log('🔧 DEBUG useEffect:', { 
      isMobile, 
      conversationsLength: conversations.length, 
      showDrawer, 
      hasUserClosedDrawer 
    });
    if (isMobile && conversations.length > 0 && !showDrawer && !hasUserClosedDrawer) {
      console.log('🔧 DEBUG: Ouverture automatique du drawer');
      setShowDrawer(true);
    }
  }, [isMobile, conversations.length]); // Retiré showDrawer de la dépendance pour éviter la boucle

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-cyan-400 border-t-transparent"></div>
          <span className="text-emerald-300">Chargement des messages cosmiques...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-emerald-950 to-gray-900">
      {/* Modal Nouvelle Conversation */}
      {showNewConversation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm">
          <div className="bg-emerald-950 rounded-xl shadow-xl p-6 w-full max-w-md mx-4 relative">
            <button 
              className="absolute top-3 right-3 text-emerald-300 hover:text-red-400 text-xl" 
              onClick={() => setShowNewConversation(false)}
            >
              &times;
            </button>
            <h2 className="text-2xl font-bold text-emerald-100 mb-4 flex items-center">
              🚀 Nouvelle conversation
            </h2>
            <input
              type="text"
              value={searchTerm}
              onChange={e => handleSearchUsers(e.target.value)}
              placeholder="Rechercher un utilisateur..."
              className="w-full mb-4 px-4 py-2 rounded-lg bg-gray-800/70 border border-emerald-800/50 text-emerald-100 placeholder-emerald-300/60 focus:ring-2 focus:ring-cyan-400/70"
              autoFocus
            />
            <div className="max-h-56 overflow-y-auto mb-4 scrollbar-hide">
              {searchResults.length === 0 && searchTerm.trim() && (
                <div className="text-emerald-300/60 text-center py-4">Aucun utilisateur trouvé</div>
              )}
              {searchResults.map(user => (
                <button
                  key={user.id}
                  className={`w-full flex items-center space-x-3 p-3 rounded-lg mb-2 transition-colors hover:bg-emerald-800/30 text-left`}
                  onClick={() => setSelectedUser(user)}
                >
                  <span className="text-2xl">🐱‍🚀</span>
                  <span className="flex-1">
                    <span className="font-semibold text-emerald-100">
                      {user.displayName || user.username || `Utilisateur ${user.id.slice(-4)}`}
                    </span>
                    <span className="block text-emerald-300/60 text-xs">@{user.username || user.id}</span>
                  </span>
                </button>
              ))}
            </div>
            <button
              className="cosmic-button w-full py-2 mt-2 disabled:opacity-50"
              onClick={() => {
                if (selectedUser) {
                  startNewConversation(selectedUser.id);
                  setShowNewConversation(false);
                  setSelectedUser(null);
                }
              }}
              disabled={!selectedUser}
            >
              Démarrer la conversation
            </button>
          </div>
        </div>
      )}

      {/* Layout responsive principal */}
      <div className="flex messages-container-mobile overflow-hidden">
        {/* Mobile Menu Button */}
        {isMobile && selectedConversation && (
          <button 
            className="fixed top-4 left-4 z-40 bg-emerald-700/80 p-2 rounded-full shadow-lg md:hidden" 
            onClick={() => setShowDrawer(true)}
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        )}

        {/* Mobile Overlay */}
        {isMobile && showDrawer && (
          <div 
            className="fixed inset-0 bg-black/50 z-[55]" 
            onClick={() => {
              console.log('🔧 DEBUG: Overlay cliqué');
              setShowDrawer(false);
              setHasUserClosedDrawer(true);
            }}
          />
        )}

        {/* Sidebar - Conversations (Mobile only) */}
        {isMobile && (
          <aside 
            className={`
              fixed inset-y-0 left-0 z-[60]
              ${!showDrawer ? '-translate-x-full' : 'translate-x-0'}
              w-80 max-w-[85vw]
              cosmic-card 
              flex flex-col 
              transition-transform duration-300 ease-in-out
              bg-emerald-950/95 backdrop-blur-xl shadow-2xl
            `}
            onClick={(e) => e.stopPropagation()}
          >
          <div className="flex justify-between items-center p-4 border-b border-emerald-800/30 bg-emerald-950/60 backdrop-blur-xl sticky top-0 z-30">
            <h1 className="text-xl font-bold text-emerald-100 flex items-center">
              💬 <span className="ml-2">Messages privés</span>
              {unreadCount > 0 && (
                <span className="ml-2 bg-cyan-400 text-gray-900 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center neon-text">
                  {unreadCount}
                </span>
              )}
            </h1>
            <div className="flex items-center space-x-2">
              <button 
                onClick={() => setShowNewConversation(!showNewConversation)} 
                className="cosmic-button-secondary p-2" 
                title="Nouvelle conversation"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </button>
              {isMobile && (
                <button 
                  className="cosmic-button-secondary p-2" 
                  onClick={(e) => {
                    console.log('🔧 DEBUG: Bouton fermer cliqué');
                    console.log('🔧 DEBUG: showDrawer avant:', showDrawer);
                    e.preventDefault();
                    e.stopPropagation();
                    setShowDrawer(false);
                    setHasUserClosedDrawer(true);
                    console.log('🔧 DEBUG: setShowDrawer(false) et setHasUserClosedDrawer(true) appelés');
                  }} 
                  title="Fermer"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
          
          {/* Recherche dans les conversations */}
          <div className="p-4 border-b border-emerald-800/20 bg-emerald-950/40">
            <input
              type="text"
              value={conversationSearchTerm}
              onChange={e => setConversationSearchTerm(e.target.value)}
              placeholder="Rechercher une conversation..."
              className="w-full px-3 py-2 rounded-lg bg-gray-800/70 border border-emerald-800/30 text-emerald-100 placeholder-emerald-300/60 focus:ring-2 focus:ring-cyan-400/70"
            />
          </div>
          
          {/* Liste des conversations */}
          <div className="flex-1 overflow-y-auto p-2 scrollbar-hide relative z-10">
            {conversations.length === 0 ? (
              <div className="text-emerald-300/60 text-center py-8">Aucune conversation</div>
            ) : (
              conversations
                .filter(conv => {
                  // Filtre par nom d'utilisateur ou ID
                  const otherId = conv.participants.find((p: string) => p !== user?.id);
                  const name = `Utilisateur ${otherId?.slice(-4)}`;
                  return (
                    !conversationSearchTerm.trim() ||
                    name.toLowerCase().includes(conversationSearchTerm.toLowerCase()) ||
                    otherId?.toLowerCase().includes(conversationSearchTerm.toLowerCase())
                  );
                })
                .sort((a, b) => {
                  // Tri par date du dernier message
                  const aTime = a.lastMessageTimestamp ? new Date(a.lastMessageTimestamp).getTime() : 0;
                  const bTime = b.lastMessageTimestamp ? new Date(b.lastMessageTimestamp).getTime() : 0;
                  return bTime - aTime;
                })
                .map(conv => {
                  const otherId = conv.participants.find((p: string) => p !== user?.id);
                  const isSelected = selectedConversation?.id === conv.id;
                  const unread = conv.unreadCount > 0;
                  return (
                    <button
                      key={conv.id}
                      className={`w-full flex items-center space-x-3 p-3 rounded-lg mb-2 transition-colors text-left ${
                        isSelected ? 'bg-emerald-800/30' : 'hover:bg-emerald-800/20'
                      }`}
                      onClick={() => {
                        selectConversation(conv.id);
                        if (isMobile) setShowDrawer(false);
                      }}
                    >
                      <span className="text-2xl">🐱‍🚀</span>
                      <span className="flex-1 min-w-0">
                        <span className="font-semibold text-emerald-100">
                          {`Utilisateur ${otherId?.slice(-4)}`}
                        </span>
                        <span className="block text-emerald-300/60 text-xs">@{otherId}</span>
                        {conv.lastMessage && (
                          <span className="block text-emerald-300/40 text-xs truncate">
                            {conv.lastMessage.content}
                          </span>
                        )}
                      </span>
                      <span className="flex flex-col items-end">
                        {conv.lastMessageTimestamp && (
                          <span className="text-emerald-400/60 text-xs">
                            {formatTime(conv.lastMessageTimestamp)}
                          </span>
                        )}
                        {unread && (
                          <span className="bg-cyan-400 text-gray-900 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center neon-text mt-1">
                            {conv.unreadCount}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })
            )}
          </div>
        </aside>
        )}

        {/* Desktop Layout - Conversations en sidebar gauche dans le contenu principal */}
        {!isMobile && (
          <div className="w-80 xl:w-96 cosmic-card border-r border-emerald-800/30 flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-emerald-800/30 bg-emerald-950/60 backdrop-blur-xl sticky top-0 z-30">
              <h1 className="text-xl font-bold text-emerald-100 flex items-center">
                💬 <span className="ml-2">Messages privés</span>
                {unreadCount > 0 && (
                  <span className="ml-2 bg-cyan-400 text-gray-900 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center neon-text">
                    {unreadCount}
                  </span>
                )}
              </h1>
              <button 
                onClick={() => setShowNewConversation(!showNewConversation)} 
                className="cosmic-button-secondary p-2" 
                title="Nouvelle conversation"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </button>
            </div>
            
            {/* Recherche dans les conversations */}
            <div className="p-4 border-b border-emerald-800/20 bg-emerald-950/40">
              <input
                type="text"
                value={conversationSearchTerm}
                onChange={e => setConversationSearchTerm(e.target.value)}
                placeholder="Rechercher une conversation..."
                className="w-full px-3 py-2 rounded-lg bg-gray-800/70 border border-emerald-800/30 text-emerald-100 placeholder-emerald-300/60 focus:ring-2 focus:ring-cyan-400/70"
              />
            </div>
            
            {/* Liste des conversations */}
            <div className="flex-1 overflow-y-auto p-2 scrollbar-hide relative z-10">
              {conversations.length === 0 ? (
                <div className="text-emerald-300/60 text-center py-8">Aucune conversation</div>
              ) : (
                conversations
                  .filter(conv => {
                    // Filtre par nom d'utilisateur ou ID
                    const otherId = conv.participants.find((p: string) => p !== user?.id);
                    const name = `Utilisateur ${otherId?.slice(-4)}`;
                    return (
                      !conversationSearchTerm.trim() ||
                      name.toLowerCase().includes(conversationSearchTerm.toLowerCase()) ||
                      otherId?.toLowerCase().includes(conversationSearchTerm.toLowerCase())
                    );
                  })
                  .sort((a, b) => {
                    // Tri par date du dernier message
                    const aTime = a.lastMessageTimestamp ? new Date(a.lastMessageTimestamp).getTime() : 0;
                    const bTime = b.lastMessageTimestamp ? new Date(b.lastMessageTimestamp).getTime() : 0;
                    return bTime - aTime;
                  })
                  .map(conv => {
                    const otherId = conv.participants.find((p: string) => p !== user?.id);
                    const isSelected = selectedConversation?.id === conv.id;
                    const unread = conv.unreadCount > 0;
                    return (
                      <button
                        key={conv.id}
                        className={`w-full flex items-center space-x-3 p-3 rounded-lg mb-2 transition-colors text-left ${
                          isSelected ? 'bg-emerald-800/30' : 'hover:bg-emerald-800/20'
                        }`}
                        onClick={() => {
                          selectConversation(conv.id);
                        }}
                      >
                        <span className="text-2xl">🐱‍🚀</span>
                        <span className="flex-1 min-w-0">
                          <span className="font-semibold text-emerald-100">
                            {`Utilisateur ${otherId?.slice(-4)}`}
                          </span>
                          <span className="block text-emerald-300/60 text-xs">@{otherId}</span>
                          {conv.lastMessage && (
                            <span className="block text-emerald-300/40 text-xs truncate">
                              {conv.lastMessage.content}
                            </span>
                          )}
                        </span>
                        {unread && (
                          <span className="bg-cyan-400 text-gray-900 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center neon-text">
                            {conv.unreadCount}
                          </span>
                        )}
                      </button>
                    );
                  })
              )}
            </div>
          </div>
        )}

        {/* Chat Area */}
        <main className="flex-1 cosmic-card flex flex-col bg-emerald-950/60 backdrop-blur-xl overflow-hidden h-full">
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-emerald-800/30 flex items-center justify-between sticky top-0 z-30 bg-emerald-950/60 backdrop-blur-xl">
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-cyan-400 rounded-full flex items-center justify-center animate-glow">
                      {getParticipantInfo(selectedConversation).avatar}
                    </div>
                    {getParticipantInfo(selectedConversation).isOnline && (
                      <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-400 rounded-full border border-gray-900"></div>
                    )}
                  </div>
                  <div>
                    <h3 className="text-emerald-100 font-medium">
                      {getParticipantInfo(selectedConversation).name}
                    </h3>
                    <p className="text-emerald-300/60 text-sm">
                      @{getParticipantInfo(selectedConversation).id}
                    </p>
                    <p className="text-emerald-300/60 text-xs">
                      {getParticipantInfo(selectedConversation).isOnline ? 'En ligne' : 'Hors ligne'}
                    </p>
                  </div>
                </div>
                <div className="flex space-x-2 relative">
                  <button 
                    className="cosmic-button-secondary p-2" 
                    onClick={() => setShowMenu(!showMenu)} 
                    title="Actions"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                    </svg>
                  </button>
                  {showMenu && (
                    <div className="absolute right-0 top-10 bg-emerald-950/90 backdrop-blur-xl rounded-lg shadow-lg border border-emerald-800/30 z-50 min-w-[180px]">
                      <button 
                        className="w-full text-left px-4 py-2 hover:bg-emerald-800/30 text-emerald-100" 
                        onClick={() => {setToast('Conversation supprimée (mock)'); setShowMenu(false);}}
                      >
                        🗑️ Supprimer
                      </button>
                      <button 
                        className="w-full text-left px-4 py-2 hover:bg-emerald-800/30 text-emerald-100" 
                        onClick={() => {setToast('Utilisateur bloqué (mock)'); setShowMenu(false);}}
                      >
                        🚫 Bloquer
                      </button>
                      <button 
                        className="w-full text-left px-4 py-2 hover:bg-emerald-800/30 text-emerald-100" 
                        onClick={() => {setToast('Conversation archivée (mock)'); setShowMenu(false);}}
                      >
                        📥 Archiver
                      </button>
                      <button 
                        className="w-full text-left px-4 py-2 hover:bg-emerald-800/30 text-emerald-100" 
                        onClick={() => {setToast('Signalé (mock)'); setShowMenu(false);}}
                      >
                        ⚠️ Signaler
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 flex flex-col-reverse scrollbar-hide min-h-0">
                {messages.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-4xl mb-3 animate-float">💫</div>
                    <p className="text-emerald-300/80">Aucun message</p>
                    <p className="text-emerald-400/60 text-sm">Commencez la conversation cosmique !</p>
                  </div>
                ) : (
                  messages.slice().reverse().map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.senderId === user?.id ? 'justify-end' : 'justify-start'} animate-fade-in`}
                    >
                      <div className={`max-w-xs sm:max-w-md md:max-w-lg lg:max-w-xl ${
                        message.senderId === user?.id
                          ? 'bg-gradient-to-br from-emerald-600 to-cyan-600 text-white' 
                          : 'cosmic-card bg-emerald-950/60 backdrop-blur-xl'
                      } rounded-2xl p-3 cosmic-hover-effect shadow-lg`}>
                        <div className={message.senderId === user?.id ? 'text-white' : 'text-emerald-100'}>
                          {'deleted' in message && message.deleted ? 
                            <span className="italic text-emerald-400/60">Message supprimé</span> : 
                            message.type === 'image' || message.type === 'gif' ? (
                              <img 
                                src={message.content} 
                                alt="Message media" 
                                className="max-w-full h-auto rounded-lg"
                                style={{ maxHeight: '200px', maxWidth: '300px' }}
                                loading="lazy"
                              />
                            ) : (
                              <span className="whitespace-pre-wrap">{message.content}</span>
                            )
                          }
                        </div>
                        <p className={`text-xs mt-1 ${
                          message.senderId === user?.id ? 'text-emerald-100' : 'text-emerald-400/60'
                        }`}>
                          {formatTime(message.createdAt)}
                          {message.senderId === user?.id && message.isRead && (
                            <span className="ml-2">✓✓</span>
                          )}
                        </p>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>
              
              {/* Message Input */}
              <div className="p-4 border-t border-emerald-800/30 bg-emerald-950/60 backdrop-blur-xl">
                {error && (
                  <div className="mb-3 p-2 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300 text-sm">
                    {error}
                  </div>
                )}
                <div className="flex space-x-3 items-end">
                  <button 
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)} 
                    className="cosmic-button-secondary p-2 mb-0"
                  >
                    😊
                  </button>
                  <button 
                    onClick={() => fileInputRef.current?.click()} 
                    className="cosmic-button-secondary p-2 mb-0" 
                    title="Envoyer une image"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A2 2 0 0020 6.382V6a2 2 0 00-2-2H6a2 2 0 00-2 2v.382a2 2 0 00.447 1.342L9 10m6 0v4a2 2 0 01-2 2H7a2 2 0 01-2-2v-4" />
                    </svg>
                  </button>
                  <button 
                    onClick={() => setShowGifPicker(!showGifPicker)} 
                    className="cosmic-button-secondary p-2 mb-0" 
                    title="Envoyer un GIF"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19,10.5V8.8H17.3V16.6H19V15H20.7V13.4H19V10.5M15.7,13.9L14.7,9.6H13.3L12.2,13.9L11.1,9.6H9.6L11.1,16.6H12.6L13.8,12L15,16.6H16.5L18,9.6H16.5L15.7,13.9M9,9.6H5.8V16.6H7.5V14.8H9A2.4,2.4 0 0 0 11.4,12.4V11.8A2.4,2.4 0 0 0 9,9.4M9.1,13.3H7.5V11.1H9.1A1,1 0 0 1 10.1,12.1V12.5A1,1 0 0 1 9.1,13.3Z"/>
                    </svg>
                  </button>
                  <input 
                    type="file" 
                    accept="image/*" 
                    ref={fileInputRef} 
                    className="hidden" 
                    onChange={handleAttachmentChange} 
                  />
                  <div className="flex-1 relative">
                    <input 
                      type="text" 
                      value={newMessage || ''} 
                      onChange={(e) => setNewMessage(e.target.value || '')} 
                      placeholder="Tapez votre message..." 
                      className="w-full bg-gray-800/70 border border-emerald-800/50 rounded-lg px-4 py-2 text-emerald-100 placeholder-emerald-300/60 focus:ring-2 focus:ring-cyan-400/70 backdrop-blur-sm focus:outline-none" 
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessageWithToast()} 
                    />
                    {showEmojiPicker && (
                      <div className="absolute bottom-full mb-2 left-0 cosmic-card p-3 rounded-lg bg-emerald-950/90 backdrop-blur-xl z-50 shadow-lg">
                        <div className="grid grid-cols-5 gap-2">
                          {cosmicEmojis.map((emoji, index) => (
                            <button 
                              key={index} 
                              onClick={() => { 
                                setNewMessage(prev => (prev || '') + emoji); 
                                setShowEmojiPicker(false); 
                              }} 
                              className="text-xl hover:scale-125 transition-transform p-1 rounded hover:bg-emerald-800/30"
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <button 
                    onClick={handleSendMessageWithToast} 
                    disabled={!(newMessage || '').trim() && !selectedGif} 
                    className="cosmic-button px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  </button>
                </div>
                
                {/* Aperçu du GIF sélectionné */}
                {selectedGif && (
                  <div className="mt-3 flex items-center space-x-3">
                    <div className="relative">
                      <img 
                        src={selectedGif} 
                        alt="GIF sélectionné" 
                        className="max-w-32 max-h-32 rounded-lg border border-emerald-800/30"
                      />
                      <button
                        onClick={removeSelectedGif}
                        className="absolute -top-2 -right-2 bg-red-500/90 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm transition-colors"
                      >
                        ×
                      </button>
                    </div>
                    <div className="text-emerald-300/80 text-sm">
                      GIF sélectionné
                    </div>
                  </div>
                )}
                
                {attachmentPreview && (
                  <div className="mt-3 flex items-center space-x-3">
                    <img 
                      src={attachmentPreview} 
                      alt="Aperçu" 
                      className="w-20 h-20 object-cover rounded-lg border border-emerald-800/30" 
                    />
                    <button 
                      onClick={handleSendAttachment} 
                      className="cosmic-button px-3 py-2"
                    >
                      Envoyer l'image
                    </button>
                    <button 
                      onClick={() => setAttachmentPreview(null)} 
                      className="cosmic-button-secondary px-3 py-2"
                    >
                      Annuler
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center">
                <div className="text-6xl mb-4 animate-float">💬</div>
                <h3 className="text-xl font-semibold text-emerald-100 mb-2">
                  Bienvenue dans l'espace privé !
                </h3>
                <p className="text-emerald-300/80 mb-4">
                  Sélectionnez une conversation pour commencer à discuter
                </p>
                <button 
                  onClick={() => setShowNewConversation(true)} 
                  className="cosmic-button"
                >
                  🚀 Nouvelle conversation
                </button>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* GIF Picker */}
      <GifPicker
        isOpen={showGifPicker}
        onSelectGif={handleGifSelect}
        onClose={() => setShowGifPicker(false)}
      />

      {/* Toast notification */}
      {toast && (
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-emerald-700/90 text-white px-6 py-3 rounded-xl shadow-lg z-50 animate-fade-in">
          {toast}
        </div>
      )}
    </div>
  );
};
